import { useState } from 'react';
import { ScanResult } from '../types/mockdetector.types';
import { DatabaseScanResult } from '../types/database.types';

// Priority mapping for sorting violations by severity
const SEVERITY_PRIORITY = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1
};

export const useMockDetectorStorage = (storagePrefix: string = '') => {
  const STORAGE_KEY = `${storagePrefix}mockdetector-latest-scan`;
  // Keep full scan in memory (no truncation)
  const [inMemoryScan, setInMemoryScan] = useState<ScanResult | DatabaseScanResult | null>(null);

  const saveLatestScan = async (scanResult: ScanResult | DatabaseScanResult): Promise<ScanResult | DatabaseScanResult> => {
    console.log('💾 Saving scan to localStorage...');
    
    // Always keep full scan in memory
    setInMemoryScan(scanResult);
    
    try {
      // Try to store the FULL scan first
      const serialized = JSON.stringify(scanResult);
      localStorage.setItem(STORAGE_KEY, serialized);
      console.log('✅ Full scan saved to localStorage');
      return scanResult;
    } catch (error) {
      // If localStorage quota exceeded, try storing truncated version
      console.warn('⚠️ localStorage quota exceeded, storing truncated scan');
      
      try {
        let truncatedResult: ScanResult | DatabaseScanResult;
        
        if ('mockDataViolations' in scanResult) {
          // Frontend scan - limit to 10k violations
          const limited = {
            ...scanResult,
            mockDataViolations: scanResult.mockDataViolations.slice(0, 10000),
            wasPartial: scanResult.mockDataViolations.length > 10000
          };
          truncatedResult = limited;
        } else {
          // Database scan - limit violations
          const limited = {
            ...scanResult,
            violations: scanResult.violations.slice(0, 10000),
            wasPartial: scanResult.violations.length > 10000
          };
          truncatedResult = limited;
        }
        
        const serialized = JSON.stringify(truncatedResult);
        localStorage.setItem(STORAGE_KEY, serialized);
        console.log('✅ Truncated scan saved (10k violations max)');
        return truncatedResult;
        
      } catch (secondError) {
        // If still too large, store only critical errors (top 4000)
        console.warn('⚠️ Still too large, storing only critical errors');
        
        let criticalOnlyResult: ScanResult | DatabaseScanResult;
        
        if ('mockDataViolations' in scanResult) {
          const sorted = [...scanResult.mockDataViolations].sort((a, b) => {
            const priorityA = SEVERITY_PRIORITY[a.severity as keyof typeof SEVERITY_PRIORITY] || 0;
            const priorityB = SEVERITY_PRIORITY[b.severity as keyof typeof SEVERITY_PRIORITY] || 0;
            return priorityB - priorityA;
          });
          
          criticalOnlyResult = {
            ...scanResult,
            mockDataViolations: sorted.slice(0, 4000),
            wasPartial: true
          };
        } else {
          const sorted = [...scanResult.violations].sort((a, b) => {
            const priorityA = SEVERITY_PRIORITY[a.severity as keyof typeof SEVERITY_PRIORITY] || 0;
            const priorityB = SEVERITY_PRIORITY[b.severity as keyof typeof SEVERITY_PRIORITY] || 0;
            return priorityB - priorityA;
          });
          
          criticalOnlyResult = {
            ...scanResult,
            violations: sorted.slice(0, 4000),
            wasPartial: true
          };
        }
        
        try {
          const serialized = JSON.stringify(criticalOnlyResult);
          localStorage.setItem(STORAGE_KEY, serialized);
          console.log('✅ Critical errors only saved (4k max)');
          return criticalOnlyResult;
        } catch (finalError) {
          console.error('❌ Could not save to localStorage even after truncation');
          // Still return the full in-memory scan
          return scanResult;
        }
      }
    }
  };

  const loadLatestScan = (): ScanResult | DatabaseScanResult | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;

      const parsed = JSON.parse(stored);
      console.log('📥 Loaded scan from localStorage');
      return parsed;
    } catch (error) {
      console.error('Failed to load scan from localStorage:', error);
      return null;
    }
  };

  const clearAllScans = (): void => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setInMemoryScan(null);
      console.log('🗑️ All scans cleared');
    } catch (error) {
      console.error('Failed to clear scans:', error);
    }
  };

  // Get the full un-truncated scan from memory
  const getFullScan = (): ScanResult | DatabaseScanResult | null => {
    return inMemoryScan;
  };

  return {
    saveLatestScan,
    loadLatestScan,
    clearAllScans,
    getFullScan
  };
};
